import {StringArray} from '@src/@interfaces/types';
import {IURLGenerator} from '@src/@interfaces/services/URLGenerator/IURLGenerator';
import {IRequestParam} from '@src/@interfaces/services/URLGenerator/IRequestParam';
import app from '@src/helpers/app';
import {IApiRoute, IClientRoute} from '@src/@interfaces/services/Router';

export default class URLGenerator implements IURLGenerator {
    getRouteByName(routeName: string): IClientRoute | IApiRoute {
        const routers = [
            () => app('api-router'),
            () => app('client-router'),
        ];

        for (const routerResolver of routers) {
            const route = routerResolver().getRouteByName(routeName);
            if (route) {
                return route;
            }
        }

        throw new Error(`Route with name [${routeName}] not found`);
    }

    route(routeName: string, params: (string | number)[] | Record<string, string | number> = []): string | null {
        try {
            return this.getRouteByName(routeName).resolveUrl(params);
        } catch (e) {
            return null;
        }
    }

    generate(pattern: string, params: (string | number)[] | Record<string, string | number> = {}): string {
        let result = pattern;
        if (Array.isArray(params)) {
            params.forEach(
                val => result = result.replace(/(:[^\/]+|{[^\/]+})\??/, String(val))
            );
        } else if (typeof params === 'object') {
            Object.keys(params).forEach(key => {
                const replaceValue = String(params[key]);

                return result = result
                    .replace(`:${key}?`, replaceValue)// client route
                    .replace(`:${key}`, replaceValue)
                    .replace(`{${key}?}`, replaceValue)// api route
                    .replace(`{${key}}`, replaceValue);
            });
        }

        return result;
    }

    matches(requestUrl: string, routeUrl: string): boolean {
        try {
            this.getRequestParams(requestUrl, routeUrl);

            return true;
        } catch (e) {
            return false;
        }
    }

    getRequestParams(requestUrl: string, routeUrl: string): IRequestParam[] {
        const urlToArray = (url: string): string[] => url.split(/[/#]+?/g).filter(part => part.trim().length);

        const params = [];
        const defaultParams: StringArray<any> = {};

        const request = urlToArray(requestUrl);
        const route = urlToArray(routeUrl);

        for (let i = 0; i < route.length; i++) {
            const routePart = route[i];
            const requestPart = i in request ? request[i] : undefined;
            const isAsterisk = routePart.charAt(0) === '*';
            const isVariable = isAsterisk || routePart.charAt(0) === ':';
            const isRequire = !isVariable || routePart.charAt(routePart.length - 1) !== '?';

            if (!isVariable) {
                if (routePart !== requestPart) {
                    throw new Error('URLs do not match');
                }

                continue;
            }

            let paramName;
            if (isAsterisk) {
                paramName = params.length;
            } else {
                paramName = routePart.substring(1, isRequire ? routePart.length - 1 : undefined);
            }

            const param: IRequestParam = {
                name: paramName,
                value: requestPart
            };

            if (!param.value && param.name in defaultParams) {
                param.value = defaultParams[param.name];
            }

            if (isRequire && !param.value) {
                throw new Error('URLs do not match');
            }

            params.push(param);
        }

        if (route.length in request) {
            throw new Error('URLs do not match');
        }

        return params;
    }

    toAbsoluteURL(uri: string): string {
        const host = app('config').get('client.app-url') ?? '';

        const sanitizedHost = host.replace(/\/$/ui, '');
        const sanitizedUri = uri
            .replace(host, '')
            .replace(/^\//ui, '');

        return `${sanitizedHost}/${sanitizedUri}`;
    }
}
